import React, { useState } from "react";
import api, { errMsg } from "../api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Lock, Unlock, KeyRound, X, ShieldQuestion, AlertCircle } from "lucide-react";

/**
 * NotePinDialog — Multi-mode dialog used for:
 *  - Setting an initial PIN (mode='set', encrypted=false)
 *  - Unlocking an encrypted note (mode='unlock')
 *  - Changing a PIN (mode='change')
 *  - Removing PIN protection (mode='remove')
 *  - Filing a "forgot PIN" reset request (mode='forgot')
 *
 * Props:
 *   note         : { id, title, is_encrypted, pin_mode, owner_id }
 *   currentUserId: string  — to differentiate owner vs shared user UI
 *   onUnlocked   : ({ html, content, drawing_data, dek }) => void
 *   onChanged    : () => void   — generic refresh trigger
 *   onClose      : () => void
 *   lang         : "de" | "en"
 */
export default function NotePinDialog({ note, currentUserId, onUnlocked, onChanged, onClose, lang = "de" }) {
    const [mode, setMode] = useState(note?.is_encrypted ? "unlock" : "set");
    const [pin, setPin] = useState("");
    const [newPin, setNewPin] = useState("");
    const [confirmPin, setConfirmPin] = useState("");
    const [pinMode, setPinMode] = useState("shared"); // for "set" only
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState("");
    const [showForgot, setShowForgot] = useState(false);

    const isOwner = note?.owner_id === currentUserId;
    const t = (de, en) => (lang === "de" ? de : en);

    if (!note) return null;

    const action = async (run, okMsg) => {
        if (busy) return;
        setBusy(true);
        try {
            const result = await run();
            if (okMsg) toast.success(okMsg);
            return result;
        } catch (e) {
            toast.error(errMsg(e, t("Fehler", "Error")));
            return null;
        } finally { setBusy(false); }
    };

    const doSet = async () => {
        if (pin.length < 4) return toast.error(t("Mindestens 4 Zeichen", "Min 4 chars"));
        if (pin !== confirmPin) return toast.error(t("PINs stimmen nicht überein", "PINs do not match"));
        const r = await action(
            () => api.post(`/notes/${note.id}/pin/set`, { pin, mode: pinMode }),
            t("PIN aktiviert", "PIN enabled")
        );
        if (r) { onChanged?.(); onClose?.(); }
    };

    const doUnlock = async () => {
        if (!pin) return;
        const r = await action(() => api.post(`/notes/${note.id}/pin/verify`, { pin }));
        if (r) {
            onUnlocked?.(r.data);
            onClose?.();
        }
    };

    const doChange = async () => {
        if (newPin.length < 4) return toast.error(t("Mindestens 4 Zeichen", "Min 4 chars"));
        if (newPin !== confirmPin) return toast.error(t("PINs stimmen nicht überein", "PINs do not match"));
        const r = await action(
            () => api.post(`/notes/${note.id}/pin/change`, { current_pin: pin, new_pin: newPin }),
            t("PIN geändert", "PIN changed")
        );
        if (r) onClose?.();
    };

    const doRemove = async () => {
        if (!window.confirm(t("PIN-Schutz wirklich entfernen?", "Really remove PIN protection?"))) return;
        const r = await action(
            () => api.delete(`/notes/${note.id}/pin`, { data: { pin } }),
            t("PIN-Schutz entfernt", "PIN removed")
        );
        if (r) { onChanged?.(); onClose?.(); }
    };

    const doForgot = async () => {
        const r = await action(
            () => api.post(`/notes/${note.id}/pin/forgot`, { reason }),
            t("Reset angefragt. Ein Admin wird benachrichtigt.", "Reset requested. An admin will be notified.")
        );
        if (r) onClose?.();
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="note-pin-dialog">
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {note.is_encrypted ? <Lock className="w-4 h-4 text-brand-500" /> : <KeyRound className="w-4 h-4 text-brand-500" />}
                        <p className="font-semibold text-sm">
                            {mode === "set" && t("PIN-Schutz aktivieren", "Enable PIN protection")}
                            {mode === "unlock" && t("PIN eingeben", "Enter PIN")}
                            {mode === "change" && t("PIN ändern", "Change PIN")}
                            {mode === "remove" && t("PIN-Schutz entfernen", "Remove PIN protection")}
                        </p>
                    </div>
                    <button onClick={onClose} className="hover:bg-neutral-100 rounded p-1" data-testid="pin-dlg-close"><X className="w-4 h-4" /></button>
                </header>

                <div className="p-4 space-y-3">
                    <p className="text-xs text-neutral-500">{note.title || t("Unbenannte Notiz", "Untitled note")}</p>

                    {/* SET mode */}
                    {mode === "set" && (
                        <>
                            {note.shared_with && note.shared_with.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold mb-1">{t("Modus für geteilte Notiz", "Mode for shared note")}</p>
                                    <div className="space-y-1.5">
                                        <label className="flex items-start gap-2 text-xs cursor-pointer p-2 border border-neutral-200 rounded hover:bg-neutral-50">
                                            <input type="radio" checked={pinMode === "shared"} onChange={() => setPinMode("shared")} data-testid="pin-mode-shared" className="mt-0.5" />
                                            <div>
                                                <p className="font-semibold">{t("Einheitliche PIN", "Single shared PIN")}</p>
                                                <p className="text-neutral-500">{t("Alle Nutzer verwenden dieselbe PIN.", "Everyone uses the same PIN.")}</p>
                                            </div>
                                        </label>
                                        <label className="flex items-start gap-2 text-xs cursor-pointer p-2 border border-neutral-200 rounded hover:bg-neutral-50">
                                            <input type="radio" checked={pinMode === "individual"} onChange={() => setPinMode("individual")} data-testid="pin-mode-individual" className="mt-0.5" />
                                            <div>
                                                <p className="font-semibold">{t("Eigene PIN pro Nutzer", "Per-user PIN")}</p>
                                                <p className="text-neutral-500">{t("Jeder geteilte Nutzer setzt seine eigene PIN beim ersten Öffnen.", "Each shared user sets their own PIN on first open.")}</p>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            )}
                            <PinInput value={pin} onChange={setPin} testId="pin-input-new" placeholder={t("Neue PIN (min. 4 Zeichen)", "New PIN (min 4 chars)")} />
                            <PinInput value={confirmPin} onChange={setConfirmPin} testId="pin-input-confirm" placeholder={t("PIN bestätigen", "Confirm PIN")} />
                            <Button onClick={doSet} className="w-full bg-brand-500 hover:bg-brand-600 text-white" disabled={busy} data-testid="pin-save">
                                <Lock className="w-3.5 h-3.5 mr-1.5" />
                                {t("Verschlüsseln & schützen", "Encrypt & protect")}
                            </Button>
                        </>
                    )}

                    {/* UNLOCK mode */}
                    {mode === "unlock" && !showForgot && (
                        <>
                            <PinInput value={pin} onChange={setPin} testId="pin-input" placeholder={t("PIN eingeben…", "Enter PIN…")} autoFocus onEnter={doUnlock} />
                            <Button onClick={doUnlock} className="w-full bg-brand-500 hover:bg-brand-600 text-white" disabled={busy || !pin} data-testid="pin-unlock">
                                <Unlock className="w-3.5 h-3.5 mr-1.5" />
                                {t("Entsperren", "Unlock")}
                            </Button>
                            <div className="flex flex-col gap-1.5 pt-2 border-t border-neutral-100">
                                <button onClick={() => setShowForgot(true)} className="text-xs text-brand-500 hover:underline text-left" data-testid="pin-forgot-link">
                                    <ShieldQuestion className="w-3 h-3 inline mr-1" />
                                    {t("PIN vergessen?", "Forgot PIN?")}
                                </button>
                                {isOwner && (
                                    <>
                                        <button onClick={() => { setMode("change"); setPin(""); setNewPin(""); setConfirmPin(""); }} className="text-xs text-neutral-500 hover:underline text-left" data-testid="pin-go-change">
                                            {t("PIN ändern", "Change PIN")}
                                        </button>
                                        <button onClick={() => { setMode("remove"); setPin(""); }} className="text-xs text-red-500 hover:underline text-left" data-testid="pin-go-remove">
                                            {t("PIN-Schutz entfernen", "Remove PIN protection")}
                                        </button>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {mode === "unlock" && showForgot && (
                        <>
                            <div className="bg-amber-50 border border-amber-200 rounded p-2 flex gap-2 text-xs">
                                <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <p>{t("Ein Admin oder Master wird informiert und kann eine neue PIN für dich freischalten.", "An admin or master will be notified and can issue a new PIN for you.")}</p>
                            </div>
                            <textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder={t("Grund (optional)", "Reason (optional)")}
                                rows={3}
                                className="w-full text-sm border border-neutral-200 rounded p-2 resize-none"
                                data-testid="pin-forgot-reason"
                            />
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setShowForgot(false)} className="flex-1">{t("Zurück", "Back")}</Button>
                                <Button onClick={doForgot} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" disabled={busy} data-testid="pin-forgot-submit">
                                    {t("Reset anfragen", "Request reset")}
                                </Button>
                            </div>
                        </>
                    )}

                    {/* CHANGE mode */}
                    {mode === "change" && (
                        <>
                            <PinInput value={pin} onChange={setPin} testId="pin-current" placeholder={t("Aktuelle PIN", "Current PIN")} />
                            <PinInput value={newPin} onChange={setNewPin} testId="pin-new" placeholder={t("Neue PIN", "New PIN")} />
                            <PinInput value={confirmPin} onChange={setConfirmPin} testId="pin-confirm" placeholder={t("Neue PIN bestätigen", "Confirm new PIN")} />
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setMode("unlock")} className="flex-1">{t("Zurück", "Back")}</Button>
                                <Button onClick={doChange} className="flex-1 bg-brand-500 hover:bg-brand-600 text-white" disabled={busy} data-testid="pin-change-submit">
                                    {t("Ändern", "Change")}
                                </Button>
                            </div>
                        </>
                    )}

                    {/* REMOVE mode */}
                    {mode === "remove" && (
                        <>
                            <p className="text-xs text-neutral-600">{t("Gib deine aktuelle PIN ein, um den Schutz zu entfernen. Inhalt wird wieder unverschlüsselt gespeichert.", "Enter your current PIN to remove protection. Content will be stored unencrypted.")}</p>
                            <PinInput value={pin} onChange={setPin} testId="pin-remove-current" placeholder={t("Aktuelle PIN", "Current PIN")} />
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setMode("unlock")} className="flex-1">{t("Zurück", "Back")}</Button>
                                <Button onClick={doRemove} className="flex-1 bg-red-500 hover:bg-red-600 text-white" disabled={busy || !pin} data-testid="pin-remove-submit">
                                    <Unlock className="w-3.5 h-3.5 mr-1.5" />
                                    {t("Schutz entfernen", "Remove protection")}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function PinInput({ value, onChange, testId, placeholder, autoFocus, onEnter }) {
    return (
        <input
            type="password"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
            className="w-full h-9 border border-neutral-200 rounded px-3 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-300"
            data-testid={testId}
            autoComplete="new-password"
        />
    );
}
